import { prefersReducedMotion } from '@theme/utilities';

/**
 * Cycles a headline through a list of words.
 *
 * The words are all present in the markup, so the first one renders before this
 * script runs and the rest stay in the DOM for crawlers. Everything below only
 * upgrades that static state.
 */
class RotatingHeadline extends HTMLElement {
  /** @type {HTMLElement[]} */
  #words = [];
  /** @type {HTMLElement | null} */
  #rotator = null;
  #index = 0;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  #visible = true;
  #typing = false;

  connectedCallback() {
    this.#rotator = this.querySelector('.rotating-headline__rotator');
    this.#words = Array.from(this.querySelectorAll('.rotating-headline__word'));
    if (!this.#rotator || this.#words.length < 2) return;

    this.classList.add('is-ready');
    this.#activate(0);

    // Pause off-screen and on hidden tabs — a headline nobody can see should
    // not keep queueing timers.
    this.#observer = new IntersectionObserver(
      ([entry]) => {
        this.#visible = entry?.isIntersecting ?? true;
        this.#visible ? this.#schedule() : this.#stop();
      },
      { threshold: 0 }
    );
    this.#observer.observe(this);
    document.addEventListener('visibilitychange', this.#onVisibility);
  }

  disconnectedCallback() {
    this.#stop();
    this.#observer?.disconnect();
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  #onVisibility = () => {
    document.hidden ? this.#stop() : this.#schedule();
  };

  get #effect() {
    return prefersReducedMotion() ? 'none' : this.dataset.effect || 'slide';
  }

  get #interval() {
    return Math.max(600, Number(this.dataset.interval) || 2600);
  }

  #stop() {
    clearTimeout(this.#timer);
    this.#timer = undefined;
  }

  #schedule() {
    if (!this.#visible || document.hidden || this.#timer || this.#typing) return;
    this.#timer = setTimeout(() => {
      this.#timer = undefined;
      this.#advance();
    }, this.#interval);
  }

  #advance() {
    const next = (this.#index + 1) % this.#words.length;
    this.#effect === 'typewriter' ? this.#type(next) : this.#activate(next);
  }

  /**
   * Swaps the visible word and morphs the container to its width so any text
   * sitting after it slides along rather than jumping.
   * @param {number} next
   */
  #activate(next) {
    const word = this.#words[next];
    if (!word || !this.#rotator) return;

    for (const el of this.#words) el.classList.remove('is-active');
    word.classList.add('is-active');
    this.#index = next;

    // offsetWidth on a grid-stacked child is its own width, not the track's.
    this.#rotator.style.width = `${word.offsetWidth}px`;
    this.#schedule();
  }

  /**
   * Deletes the current word one character at a time, then types the next.
   * @param {number} next
   */
  async #type(next) {
    const from = this.#words[this.#index];
    const to = this.#words[next];
    if (!from || !to || !this.#rotator) return;

    this.#typing = true;
    const full = to.dataset.word ?? to.textContent ?? '';
    const start = from.dataset.word ?? from.textContent ?? '';

    for (let i = start.length; i >= 0; i--) {
      if (!this.isConnected) return;
      from.textContent = start.slice(0, i);
      this.#rotator.style.width = `${from.offsetWidth}px`;
      await wait(28);
    }

    from.textContent = start;
    this.#activate(next);
    to.textContent = '';

    for (let i = 0; i <= full.length; i++) {
      if (!this.isConnected) return;
      to.textContent = full.slice(0, i);
      this.#rotator.style.width = `${to.offsetWidth}px`;
      await wait(52);
    }

    this.#typing = false;
    this.#schedule();
  }
}

/** @param {number} ms */
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

if (!customElements.get('rotating-headline')) {
  customElements.define('rotating-headline', RotatingHeadline);
}
